// @owners { team: patients }
import { BORDERS, COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import { Body, Column, Description, XsSpacing } from '@alto/design-system';
import { useAddOnsContext } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import { type AddOnOtc } from '@alto/scriptdash/alto/patient_app/add_ons/types/v1/add_on_otc';
import React from 'react';
import styled from 'styled-components';
import { useEssentialsAddress } from '~shared/features/essentials';
import { EssentialTag } from './EssentialsTag';
import { FSABadge } from './FsaBadge';
import { StoreProductTag } from './StoreProductTag';
import { Image, StyledTouchable, type StyledTouchableProps } from '~web/features/essentials/primitives';

type Props = {
  readonly count: number;
  readonly product: AddOnOtc;
  readonly index: number;
  readonly categoryName: string;
  readonly searchQuery?: string;
  readonly isStoreItem?: boolean;
  readonly isInScrollView?: boolean;
};

/** Horizontal gap total for `count` cards in one row (supports fractional `count`, e.g. 2.5). */
const gapTotalCss = (count: number) => {
  const gap = SPACING.STATIC.SM.px;
  const gapCount = Math.max(0, count - 1);
  if (gapCount === 0) {
    return '0px';
  }
  return `calc(${gap} * ${gapCount})`;
};

/** `1/count` as a CSS multiplier — avoids `calc(... / 2.5)` which can mis-resolve for flex % sizing. */
const inverseCountCss = (count: number) => String(Number((1 / count).toPrecision(10)));

/**
 * Scroll items size against the scroll row width. `contain: inline-size` on the row (in
 * ProductScrollView) keeps `100%` anchored to the visible scrollport, not expanding content.
 */
const scrollItemBasis = (count: number, isInScrollView?: boolean) => {
  const gaps = gapTotalCss(count);
  const inv = inverseCountCss(count);
  const stripHorizontalPad = SPACING.STATIC.LG.px;
  const fromRow = `calc((100% - ${gaps}) * ${inv})`;
  if (isInScrollView) {
    return fromRow;
  }
  const fromViewport = `calc((100svw - 2 * ${stripHorizontalPad} - ${gaps}) * ${inv})`;
  return `min(${fromRow}, ${fromViewport})`;
};

const StyledScrollViewListItem = styled.li<
  { $scrollItemBasis: string; children: React.ReactNode } & StyledTouchableProps
>`
  margin: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-width: 0;
  flex: ${({ $scrollItemBasis }) => `0 0 ${$scrollItemBasis}`};
  max-width: ${({ $scrollItemBasis }) => $scrollItemBasis};
  width: ${({ $scrollItemBasis }) => $scrollItemBasis};
  border-radius: ${BORDERS.RADIUS.LG.px};
  padding: ${SPACING.STATIC.SM.px};
  &:hover {
    background-color: ${COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTER};
  }
  ${StyledTouchable}
`;

/** Matches desktop grid product header: centered image with cart badge overlaid (not in flex row flow). */
const ProductImageArea = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: ${SIZES.ILLUSTRATION.LG.px};

  img {
    display: block;
    max-width: 100%;
    height: auto;
  }
`;

/** Bounds text so RN Web Body/Description wrap and line-clamp against the card width. */
const CardCopyColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;

  /* RN Web Text nodes need a bounded width to wrap / honor numberOfLines. */
  & > * {
    max-width: 100%;
    min-width: 0;
  }
`;

export const ProductItem = ({
  count,
  product,
  categoryName,
  index,
  searchQuery,
  isStoreItem,
  isInScrollView,
}: Props) => {
  const { navigate } = useNavigation();
  const { shipmentID, origin } = useAddOnsContext();
  const address = useEssentialsAddress({ shipmentID });
  const scrollItemBasisValue = scrollItemBasis(count, isInScrollView);

  const onPress = () => {
    let search = `?origin=${origin}`;
    if (shipmentID) {
      search += `&order=${shipmentID}`;
    }
    navigate('RouteEssentialsDetails', {
      addOnOtc: product,
      productID: product.product_id,
      analyticsProps: {
        slot: index + 1,
        sku: product.sku,
        sku_type: product.sku_type,
        otc_name: product.name,
        category: categoryName,
        query_string: searchQuery,
        shipment_id: shipmentID,
        facility_id: address?.dispensing_facility_id,
        origin,
      },
      search,
    });
  };

  return (
    <StyledScrollViewListItem
      onClick={onPress}
      $scrollItemBasis={scrollItemBasisValue}
      key={`product-item-for-${product.sku}`}
    >
      <ProductImageArea>
        <Image
          src={product.image_urls[0]}
          width={SIZES.ILLUSTRATION.LG.px}
          height={SIZES.ILLUSTRATION.LG.px}
          alt={`icon for ${product.name}`}
        />
        <StoreProductTag
          product={product}
          shipmentID={shipmentID}
        />
      </ProductImageArea>
      {isStoreItem ? <FSABadge addOnOtc={product} /> : <EssentialTag addOnOtc={product} />}
      <CardCopyColumn>
        <Body
          fontFamily="semibold"
          numberOfLines={3}
          color={COLORS.TEXT_COLORS.PRIMARY}
        >
          {product.name}
        </Body>
        {/* @ts-expect-error spacer ensures footer aligns with bottom regardless of content above */}
        <Column flexGrow={1} />
        <XsSpacing />
        <Description color={COLORS.TEXT_COLORS.GREY}>{product.quantity_description || ''}</Description>
        <XsSpacing />
        <Description color={COLORS.TEXT_COLORS.PRIMARY}>{`$${product.price.toFixed(2)}`}</Description>
      </CardCopyColumn>
    </StyledScrollViewListItem>
  );
};
